import classicStoneKitJson from '../../assets/editor/dungeon-kits/classic-stone-v1.json';
import classicStoneKitV2Json from '../../assets/editor/dungeon-kits/classic-stone-v2.json';
import shameKitJson from '../../assets/editor/dungeon-kits/shame-v1.json';
import type { DungeonDirection } from './dungeon';
import {
  transformDungeonDirection,
  type DungeonCorner,
  type DungeonModuleDefinition,
  type DungeonModuleStatic,
  type DungeonModuleTransform,
  type DungeonModuleSocket,
  type DungeonStructureRole,
} from './dungeonModule';
import type { TilePoint } from './mobile';

export type { DungeonCorner } from './dungeonModule';

export interface DungeonStructurePiece {
  tileName: string;
  /** Optional terrain treatment required by this art footprint. */
  terrain?: 'void';
  /** Optional terrain used only when the authored cell below this piece is void. */
  voidUnderlay?: string;
}

export interface DungeonStructureKit {
  id: string;
  floor: string;
  /** Deterministic weighted floor vocabulary learned from the source map. */
  floorVariants?: DungeonStructureFloorVariant[];
  /** Art names recognized as replaceable structure rather than decoration. */
  sourceTiles: string[];
  walls: Record<DungeonDirection, string>;
  outerCorners: Record<DungeonCorner, DungeonStructurePiece>;
  innerCorners: Record<DungeonCorner, DungeonStructurePiece>;
  doorwayReturns: Record<DungeonCorner, DungeonStructurePiece>;
  post: DungeonStructurePiece;
}

export interface DungeonStructureFloorVariant {
  tileName: string;
  weight: number;
}

export type DungeonStructureTerrainAction =
  | { kind: 'void' }
  | { kind: 'fill'; tileName: string }
  | null;

const DIRECTIONS: DungeonDirection[] = ['north', 'east', 'south', 'west'];
const CORNERS: DungeonCorner[] = ['north-west', 'north-east', 'south-east', 'south-west'];

/**
 * The baked art vocabulary a structure kit's tile names must exist in.
 * Kept as an injected argument to {@link validateDungeonStructureKitArt} rather
 * than an import: the baked manifests grew far past what the cold entry budget
 * (ADR-0310) can carry, so only environments that own the invariant (the server
 * at startup, the tests, the editor) load them via `bakedTileCatalog.ts`.
 */
export interface BakedTileCatalog {
  landTileNames: ReadonlySet<string>;
  staticTileNames: ReadonlySet<string>;
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`invalid dungeon structure kit ${label}`);
  }
  return value as Record<string, unknown>;
}

function stringValue(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`invalid dungeon structure kit ${label}`);
  }
  return value;
}

function floorVariants(
  value: unknown,
  floor: string,
): DungeonStructureFloorVariant[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error('invalid dungeon structure kit floorVariants');
  }
  const seen = new Set<string>();
  const variants = value.map((entry, index) => {
    const source = record(entry, `floorVariants[${index}]`);
    const tileName = stringValue(source.tileName, `floorVariants[${index}].tileName`);
    if (
      !Number.isInteger(source.weight) ||
      (source.weight as number) < 1 ||
      (source.weight as number) > 1_000
    ) {
      throw new Error(`invalid dungeon structure kit floorVariants[${index}].weight`);
    }
    if (seen.has(tileName)) {
      throw new Error(`invalid dungeon structure kit duplicate floor variant ${tileName}`);
    }
    seen.add(tileName);
    return { tileName, weight: source.weight as number };
  });
  if (!seen.has(floor)) {
    throw new Error('invalid dungeon structure kit floorVariants must include floor');
  }
  return variants;
}

function piece(value: unknown, label: string): DungeonStructurePiece {
  const source = record(value, label);
  const terrain = source.terrain;
  const voidUnderlay = source.voidUnderlay;
  if (terrain !== undefined && terrain !== 'void') {
    throw new Error(`invalid dungeon structure kit ${label}.terrain`);
  }
  if (terrain === 'void' && voidUnderlay !== undefined) {
    throw new Error(`invalid dungeon structure kit ${label} terrain conflict`);
  }
  return {
    tileName: stringValue(source.tileName, `${label}.tileName`),
    ...(terrain === undefined ? {} : { terrain }),
    ...(voidUnderlay === undefined
      ? {}
      : { voidUnderlay: stringValue(voidUnderlay, `${label}.voidUnderlay`) }),
  };
}

function cornerPieces(
  value: unknown,
  label: string,
): Record<DungeonCorner, DungeonStructurePiece> {
  const source = record(value, label);
  return Object.fromEntries(
    CORNERS.map((corner) => [corner, piece(source[corner], `${label}.${corner}`)]),
  ) as Record<DungeonCorner, DungeonStructurePiece>;
}

function deepFreeze<T>(value: T): T {
  if (typeof value !== 'object' || value === null || Object.isFrozen(value)) return value;
  for (const nested of Object.values(value)) deepFreeze(nested);
  return Object.freeze(value);
}

function validateBakedLand(catalog: BakedTileCatalog, tileName: string, label: string): void {
  if (!catalog.landTileNames.has(tileName)) {
    throw new Error(`unknown dungeon structure kit land tile ${label}: ${tileName}`);
  }
}

function validateBakedStatic(catalog: BakedTileCatalog, tileName: string, label: string): void {
  if (!catalog.staticTileNames.has(tileName)) {
    throw new Error(`unknown dungeon structure kit static tile ${label}: ${tileName}`);
  }
}

function validateBakedPiece(
  catalog: BakedTileCatalog,
  value: DungeonStructurePiece,
  label: string,
): void {
  validateBakedStatic(catalog, value.tileName, `${label}.tileName`);
  if (value.voidUnderlay) validateBakedLand(catalog, value.voidUnderlay, `${label}.voidUnderlay`);
}

/**
 * Assert every tile name a kit references exists in the baked art catalogs.
 * Structural validation lives in {@link defineDungeonStructureKit}; this baked
 * cross-check is a content invariant enforced where the manifests are loaded
 * (server startup and the tests), so the client entry never pays for them.
 */
export function validateDungeonStructureKitArt(
  kit: DungeonStructureKit,
  catalog: BakedTileCatalog,
): void {
  validateBakedLand(catalog, kit.floor, 'floor');
  kit.floorVariants?.forEach((variant, index) =>
    validateBakedLand(catalog, variant.tileName, `floorVariants[${index}].tileName`));
  kit.sourceTiles.forEach((tileName, index) =>
    validateBakedStatic(catalog, tileName, `sourceTiles[${index}]`));
  DIRECTIONS.forEach((direction) =>
    validateBakedStatic(catalog, kit.walls[direction], `walls.${direction}`));
  for (const corner of CORNERS) {
    validateBakedPiece(catalog, kit.outerCorners[corner], `outerCorners.${corner}`);
    validateBakedPiece(catalog, kit.innerCorners[corner], `innerCorners.${corner}`);
    validateBakedPiece(catalog, kit.doorwayReturns[corner], `doorwayReturns.${corner}`);
  }
  validateBakedPiece(catalog, kit.post, 'post');
}

/**
 * Structurally validate and freeze a swappable art adapter before generation
 * can consume it. The baked-art cross-check is separate (see
 * {@link validateDungeonStructureKitArt}) because it needs the large baked
 * manifests, which must stay off the client's cold entry (ADR-0310).
 */
export function defineDungeonStructureKit(value: unknown): DungeonStructureKit {
  const source = record(value, 'root');
  if (!Array.isArray(source.sourceTiles) || source.sourceTiles.length === 0) {
    throw new Error('invalid dungeon structure kit sourceTiles');
  }
  const walls = record(source.walls, 'walls');
  const floor = stringValue(source.floor, 'floor');
  const kit: DungeonStructureKit = {
    id: stringValue(source.id, 'id'),
    floor,
    ...(source.floorVariants === undefined
      ? {}
      : { floorVariants: floorVariants(source.floorVariants, floor) }),
    sourceTiles: source.sourceTiles.map((tile, index) =>
      stringValue(tile, `sourceTiles[${index}]`)),
    walls: Object.fromEntries(
      DIRECTIONS.map((direction) => [direction, stringValue(walls[direction], `walls.${direction}`)]),
    ) as Record<DungeonDirection, string>,
    outerCorners: cornerPieces(source.outerCorners, 'outerCorners'),
    innerCorners: cornerPieces(source.innerCorners, 'innerCorners'),
    doorwayReturns: cornerPieces(source.doorwayReturns, 'doorwayReturns'),
    post: piece(source.post, 'post'),
  };
  return deepFreeze(kit);
}

export const CLASSIC_STONE_STRUCTURE_KIT = defineDungeonStructureKit(classicStoneKitJson);
export const CLASSIC_STONE_STRUCTURE_KIT_V2 = defineDungeonStructureKit(classicStoneKitV2Json);
export const SHAME_STRUCTURE_KIT = defineDungeonStructureKit(shameKitJson);

const DUNGEON_STRUCTURE_KITS = new Map<string, DungeonStructureKit>([
  [CLASSIC_STONE_STRUCTURE_KIT.id, CLASSIC_STONE_STRUCTURE_KIT],
  [CLASSIC_STONE_STRUCTURE_KIT_V2.id, CLASSIC_STONE_STRUCTURE_KIT_V2],
  [SHAME_STRUCTURE_KIT.id, SHAME_STRUCTURE_KIT],
]);

export const DUNGEON_STRUCTURE_KIT_IDS = Object.freeze([...DUNGEON_STRUCTURE_KITS.keys()]);

export function dungeonStructureKit(id: string): DungeonStructureKit | undefined {
  return DUNGEON_STRUCTURE_KITS.get(id);
}

/** Resolve the terrain mutation required beneath one semantic structure piece. */
export function dungeonStructureTerrainAction(
  piece: DungeonStructurePiece | null,
  currentIsVoid: boolean,
): DungeonStructureTerrainAction {
  if (piece?.terrain === 'void') return { kind: 'void' };
  if (piece?.voidUnderlay && currentIsVoid) {
    return { kind: 'fill', tileName: piece.voidUnderlay };
  }
  return null;
}

const CORNER_DIRECTIONS: Record<DungeonCorner, readonly [DungeonDirection, DungeonDirection]> = {
  'north-west': ['north', 'west'],
  'north-east': ['north', 'east'],
  'south-east': ['south', 'east'],
  'south-west': ['south', 'west'],
};

function cornerForDirections(a: DungeonDirection, b: DungeonDirection): DungeonCorner {
  const directions = new Set([a, b]);
  if (directions.has('north') && directions.has('west')) return 'north-west';
  if (directions.has('north') && directions.has('east')) return 'north-east';
  if (directions.has('south') && directions.has('east')) return 'south-east';
  if (directions.has('south') && directions.has('west')) return 'south-west';
  throw new Error(`invalid dungeon corner directions: ${a},${b}`);
}

export function transformDungeonCorner(
  corner: DungeonCorner,
  transform: DungeonModuleTransform,
): DungeonCorner {
  const [a, b] = CORNER_DIRECTIONS[corner];
  return cornerForDirections(
    transformDungeonDirection(a, transform),
    transformDungeonDirection(b, transform),
  );
}

function samePoint(a: TilePoint, b: TilePoint): boolean {
  return a.x === b.x && a.y === b.y;
}

function socketReturnPoints(
  socket: DungeonModuleSocket,
  passageWidth: number,
): Array<{ point: TilePoint; corner: DungeonCorner }> {
  const returnOffset = Math.floor(passageWidth / 2) + 1;
  switch (socket.direction) {
    case 'north':
      return [
        { point: { x: socket.position.x - returnOffset, y: socket.position.y }, corner: 'north-west' },
        { point: { x: socket.position.x + returnOffset, y: socket.position.y }, corner: 'north-east' },
      ];
    case 'east':
      return [
        { point: { x: socket.position.x, y: socket.position.y - returnOffset }, corner: 'north-east' },
        { point: { x: socket.position.x, y: socket.position.y + returnOffset }, corner: 'south-east' },
      ];
    case 'south':
      return [
        { point: { x: socket.position.x - returnOffset, y: socket.position.y }, corner: 'south-west' },
        { point: { x: socket.position.x + returnOffset, y: socket.position.y }, corner: 'south-east' },
      ];
    case 'west':
      return [
        { point: { x: socket.position.x, y: socket.position.y - returnOffset }, corner: 'north-west' },
        { point: { x: socket.position.x, y: socket.position.y + returnOffset }, corner: 'south-west' },
      ];
  }
}

function doorwayReturnCorner(
  module: DungeonModuleDefinition,
  point: TilePoint,
  openSockets: ReadonlySet<string>,
  passageWidth: number,
): DungeonCorner | null {
  for (const socket of module.sockets) {
    if (!openSockets.has(socket.id)) continue;
    const match = socketReturnPoints(socket, passageWidth).find((candidate) =>
      samePoint(candidate.point, point));
    if (match) return match.corner;
  }
  return null;
}

function outerCorner(
  module: Pick<DungeonModuleDefinition, 'width' | 'height'>,
  point: TilePoint,
): DungeonCorner | null {
  if (point.x === 0 && point.y === 0) return 'north-west';
  if (point.x === module.width - 1 && point.y === 0) return 'north-east';
  if (point.x === module.width - 1 && point.y === module.height - 1) return 'south-east';
  if (point.x === 0 && point.y === module.height - 1) return 'south-west';
  return null;
}

function inferredWallDirection(
  module: DungeonModuleDefinition,
  item: DungeonModuleStatic,
): DungeonDirection | null {
  if (item.wallDirection) return item.wallDirection;
  if (module.repeat?.direction === 'east-west') {
    const ys = module.walkable.map((point) => point.y);
    if (ys.length === 0) return null;
    return item.position.y < Math.min(...ys) ? 'north' : 'south';
  }
  if (module.repeat?.direction === 'north-south') {
    const xs = module.walkable.map((point) => point.x);
    if (xs.length === 0) return null;
    return item.position.x < Math.min(...xs) ? 'west' : 'east';
  }
  if (item.position.y === 0) return 'north';
  if (item.position.x === module.width - 1) return 'east';
  if (item.position.y === module.height - 1) return 'south';
  if (item.position.x === 0) return 'west';
  return null;
}

export function dungeonStructurePieceForRole(
  role: DungeonStructureRole,
  transform: DungeonModuleTransform,
  kit: DungeonStructureKit,
): DungeonStructurePiece {
  switch (role.kind) {
    case 'wall':
      return { tileName: kit.walls[transformDungeonDirection(role.direction, transform)] };
    case 'outer-corner':
      return kit.outerCorners[transformDungeonCorner(role.corner, transform)];
    case 'inner-corner':
      return kit.innerCorners[transformDungeonCorner(role.corner, transform)];
    case 'doorway-return':
      return kit.doorwayReturns[transformDungeonCorner(role.corner, transform)];
    case 'post':
      return kit.post;
  }
}

/**
 * Resolve one authored structural static through a swappable dungeon kit.
 * Decorations return null and retain their authored tile name.
 */
export function dungeonStructurePieceForStatic(
  module: DungeonModuleDefinition,
  item: DungeonModuleStatic,
  transform: DungeonModuleTransform,
  openSockets: ReadonlySet<string>,
  passageWidth: number,
  kit: DungeonStructureKit,
): DungeonStructurePiece | null {
  if (item.structureRole) return dungeonStructurePieceForRole(item.structureRole, transform, kit);
  if (!item.blocking) return null;
  if (!item.wallDirection && !kit.sourceTiles.includes(item.tileName)) return null;

  const returnCorner = doorwayReturnCorner(module, item.position, openSockets, passageWidth);
  if (returnCorner) {
    return kit.doorwayReturns[transformDungeonCorner(returnCorner, transform)];
  }
  const corner = outerCorner(module, item.position);
  if (corner) return kit.outerCorners[transformDungeonCorner(corner, transform)];
  const direction = inferredWallDirection(module, item);
  if (!direction) return null;
  return { tileName: kit.walls[transformDungeonDirection(direction, transform)] };
}
